import { BehaviorSubject, Subject, debounceTime } from 'rxjs';
import { CoordinatorService } from './coordinator.service';
import { XItem } from './x-item';
import { Dataset } from './dataset';
import { ResultListSubset } from './result-list-subset';
import { STG, Stage } from './stage';

export type RESULT_LIST_SUBSET = {
    idSet: Set<string>;
    isNot?: boolean;
    stage: STG;
    prefix?: string;
    title: string;
    icon?: string;
    hasCounter?: boolean;
    class?: string;
    back?: () => void;
};

export class ResultList$ {
    activeItem: XItem | undefined;
    activeList$ = new BehaviorSubject<XItem[]>([]);
    displayList: XItem[] = [];

    activeIdSet = new Set<string>();

    informItemChange$ = new Subject<void>();

    // previewMainOverlapSet = new Set<string>()
    private _generateActiveLs$ = new Subject<void>();
    private _runActiveLs$ = this._generateActiveLs$.pipe(debounceTime(100));

    private _previewResultListSubset?: ResultListSubset;
    private _mainResultListSubset?: ResultListSubset;

    constructor(public coords: CoordinatorService) {
        this._runActiveLs$.subscribe(() => this._generateActiveLs());
    }

    onStart() {}

    get displayStep() {
        let i = Math.ceil(window.innerHeight / 50);
        if (i < 10) i = 10;
        return i;
    }

    activateSubset(params: RESULT_LIST_SUBSET) {
        const rls = new ResultListSubset(this.coords, params);
        if (rls.stage === 'collection') this._mainResultListSubset = rls;
        if (rls.stage === 'preview') this._previewResultListSubset = rls;
        this.coords.stage$.set(rls.stage);
        this.markForCheck();
    }

    deactivateSubset(stg: STG) {
        let bck: (() => void) | undefined;
        if (stg === 'collection') bck = this._mainResultListSubset?.back;
        if (stg === 'preview') bck = this._previewResultListSubset?.back;

        bck && bck();

        if (stg === 'collection') this._mainResultListSubset = undefined;
        if (stg === 'preview') this._previewResultListSubset = undefined;

        this.markForCheck();
    }

    updateSubset(stg: STG, params: Partial<RESULT_LIST_SUBSET>) {
        let rls: ResultListSubset | undefined;
        if (stg === 'collection') rls = this._mainResultListSubset;
        if (stg === 'preview') rls = this._previewResultListSubset;

        if (!rls) return;
        rls.update(params);
        if (params.idSet || params.isNot !== undefined) this.markForCheck();
    }

    subset(stg: STG) {
        if (stg === 'collection') return this._mainResultListSubset;
        if (stg === 'preview') return this._previewResultListSubset;
        return undefined;
    }

    setCurrentItem(xitm: XItem) {
        this.activeItem = xitm;
        this.informItemChange$.next();
        this.coords.display$.markForCheck();
    }

    /**
     * Get counter value for a specific type.
     * @deprecated Use coords.counter$.get(type) instead for centralized counter management.
     * This method is kept for backward compatibility but Counter$ is now the source of truth.
     */
    activeCounter(
        tp: 'total' | 'active' | 'seen' | 'rejected' | 'main' | 'preview' | 'bookmarked',
    ): number {
        switch (tp) {
            case 'total':
                return this.coords.dataset$.current.size || 0;
            case 'active':
                return this.activeList$.getValue().length;
            case 'seen':
                return this._inActiveViewCounter(tp);
            case 'rejected':
                return this._inActiveViewCounter(tp);
            case 'bookmarked':
                return this._inActiveViewCounter(tp);
            case 'main':
                return this.activeList$
                    .getValue()
                    .filter((x) => this.coords.dataset$.fromPreview2MainId(x.id)).length;
            case 'preview':
                return this.activeList$
                    .getValue()
                    .filter((x) => this.coords.dataset$.fromMain2PreviewId(x.id)).length;
        }
    }

    private _inActiveViewCounter(tp: 'seen' | 'rejected' | 'bookmarked') {
        const v = this.coords.view$.get(`__${tp}`);
        return v?.countInSet(this.activeIdSet) || 0;
    }

    private _displayWindow(xitm?: XItem) {
        const actLs = this.activeList$.getValue();

        const i = xitm ? actLs.indexOf(xitm) : 0;
        let first = i - this.displayStep;
        if (first < 0) first = 0;
        this.displayList = actLs.slice(first, i + this.displayStep);
        this.coords.display$.markForCheck();
    }

    more() {
        this._displayWindow(this.displayList[this.displayList.length - 1]);
    }

    prev() {
        this._displayWindow();
    }

    markForCheck() {
        this._generateActiveLs$.next();
    }

    private _generateActiveLs() {
        const dt$ = this.coords.dataset$;

        let pTag: string;
        let dts: Dataset;

        pTag = this.coords.stage$.datasetTag;
        dts = dt$.current;

        let ls = dts.sortedMainXitemLs;

        let rls: ResultListSubset | undefined;
        if (dts.isPreview) rls = this._previewResultListSubset;
        else rls = this._mainResultListSubset;

        if (rls) {
            const s = rls.idSet;
            if (rls.isNot) ls = ls.filter((xitm) => !s.has(xitm.id));
            else ls = ls.filter((xitm) => s.has(xitm.id));
        }

        if (this.coords.field$.isActive) ls = this.coords.field$.process(ls);
        if (this.coords.selection$.isActive) ls = this.coords.selection$.process(ls);
        if (this.coords.filter$.isActive) ls = this.coords.filter$.process(ls);

        let displayLen = this.displayStep;
        let currentItem = this.activeItem;

        const cutoff = this.coords.field$.cutoff;
        if (cutoff > 0 && ls.length > cutoff) ls = ls.slice(0, cutoff);

        this.activeList$.next(ls);

        if (currentItem) {
            const i = ls.indexOf(currentItem);
            if (i >= 0 && i < 10) displayLen += i;
            else currentItem = undefined;
        }

        if (displayLen > 500) displayLen = 500;
        this.activeItem = currentItem || ls[0];
        this.displayList = ls.slice(0, displayLen);
        this.activeIdSet = new Set(ls.map((x) => x.id));
        // this.refreshPreviewMainOverlapSet()
        this.coords.display$.markForCheck();
    }

    // refreshPreviewMainOverlapSet() {
    //     const dt$ = this.coords.dataset$
    //     this.previewMainOverlapSet = new Set()
    //     for (const aId of this.activeIdSet) {
    //         const otherId = dt$.fromPreview2MainId(aId) ||
    //             dt$.fromMain2PreviewId(aId)
    //         otherId && this.previewMainOverlapSet.add(aId)
    //     }
    // }

    currentItemToSingleModeSeen() {
        if (!this.coords.dataset$.isSingleItemMode) return;
        const id = this.activeItem?.id;
        if (!id) return;
        const v = this.singleModeSeenView;
        !v.has(id) && v.addIds(id);

        if (this.coords.dataset$.current.isPreview) {
            const sv = this.coords.view$.get('__seen')!;
            !sv.has(id) && sv.addIds(id);
        }
    }

    get singleModeSeenView() {
        return this.coords.view$.get(
            `__singleModeSeenX${this.coords.dataset$.current.isPreview ? 'preview' : 'main'}`,
        )!;
    }

    resetSingleModeSeenView() {
        this.singleModeSeenView.clear();
        this.coords.display$.markForCheck();
    }

    getFutureStepItem(isPrev = false) {
        const ls = this.activeList$.getValue();
        const i = ls.indexOf(this.activeItem!);

        const finalLs = isPrev ? ls.slice(0, i - 1) : ls.slice(i);

        if (isPrev) {
            if (i === 0) return;
        } else {
            if (i === ls.length - 1) return;
        }
        return ls[i + (isPrev ? -1 : 1)];
    }

    getNextUnreadItem() {
        const ls = this.activeList$.getValue();
        const i = ls.indexOf(this.activeItem!);
        for (const xitm of ls.slice(i + 1)) {
            if (!this.singleModeSeenView.has(xitm.id)) return xitm;
        }
        return undefined;
    }

    firstItem() {
        const ls = this.activeList$.getValue();
        this.activeItem = ls[0];
        this.coords.display$.markForCheck();
    }

    step(isPrev = false) {
        const itm = this.getFutureStepItem(isPrev);
        if (!itm) return;
        this.activeItem = itm;
        this.coords.display$.markForCheck();
    }

    get stepCounter() {
        const aLs = this.activeList$.getValue();

        const seenV = this.singleModeSeenView;
        const seenLs = aLs.filter((x) => seenV.has(x.id));

        return {
            pos: aLs.indexOf(this.activeItem!) + 1,
            total: aLs.length,
            seen: seenLs.length,
            percentage: Math.floor((seenLs.length / aLs.length) * 100),
        };
    }

    // get filterMaps(): Map<'require' | 'exclude', Map<string, Map<string, Set<string>>>> {
    //     return this.coords.autotag$.getConstant('filter-map');
    // }

    // private _filterOR(mp: Map<string, Set<string>>) {
    //     const s = new Set<string>();
    //     mp.forEach((x) => x.forEach((y) => s.add(y)));
    //     return s;
    // }

    refreshStage() {
        const s$ = this.coords.search$;

        // s$.activeSearchFieldLs.forEach((sf) => sf.refreshStage());
        // s$.refresh(false);
        this.markForCheck();
        // this.clearFilters(true);
        // s$.refreshSelection(false)
        // this.coords.search$.refreshStage()
    }

    // resetAll() {
    //     this.coords.search$.clear();
    //     this.clearSelections();
    //     this.clearFilters(true);
    //     this.resetSelection();
    // }

    // clearSelections() {
    //     this.coords.values$.clearSearch();

    //     this.coords.search$.getSearchField('hitset')?.implementation?.clear();

    //     if (!this.coords.dataset$.current.isPreview) {
    //         this.coords.search$.getSearchField('view')?.implementation?.clear();
    //         this.coords.binaryTree$.list.forEach((bt) => bt.searchImpl?.clear());
    //     }

    //     this.coords.display$.markForCheck();
    // }

    // clearFilters(withRefresh = false) {
    //     const mp = this.filterMaps;
    //     mp.get('require')!.clear();
    //     mp.get('exclude')!.clear();
    //     withRefresh && this.refreshFilters();
    // }

    // refreshFilters() {
    //     const pTag = this.coords.stage$.datasetTag;

    //     const mainMp = this.filterMaps;
    //     const requireMp = mainMp.get('require')!;
    //     const excludeMp = mainMp.get('exclude')!;

    //     if (!requireMp.size && !excludeMp.size)
    //         this.coords.autotag$.deleteConstant(`filter-id-set-${pTag}`);
    //     else {
    //         const excludedSet = new Set<string>();
    //         excludeMp.forEach((x) =>
    //             x.forEach((y) => y.forEach((itmId) => excludedSet.add(itmId))),
    //         );

    //         let startSet: Set<string>;

    //         let requireLs = Array.from(requireMp.values()).filter((panelMp) => panelMp.size);

    //         if (requireLs.length) {
    //             startSet = this._filterOR(requireLs[0]);
    //             requireLs.slice(1).forEach((x) => {
    //                 const s = this._filterOR(x);
    //                 startSet = new Set(Array.from(startSet).filter((y) => s.has(y)));
    //             });
    //         } else {
    //             startSet = this.coords.dataset$.current.mainIdSet;
    //         }
    //         excludedSet.forEach((x) => startSet.delete(x));

    //         this.coords.autotag$.setConstant(`filter-id-set-${pTag}`, startSet);
    //     }
    //     this.generateActiveLs();
    // }

    requireDisplayCurrentItem() {
        const itm = this.activeItem!;
        setTimeout(() => {
            this.activeItem = itm;
            this._displayWindow(itm);
            this._scrollToActiveItem();
        }, 200);
    }

    private _scrollToActiveItem(counter = 0) {
        if (counter > 10) return;
        const actItm = this.activeItem;
        if (!actItm) return;
        const el = document.getElementById(`entry${actItm.order}`);
        if (el) el.scrollIntoView();
        else setTimeout(() => this._scrollToActiveItem(counter++), 200);
    }
}
